import { FloatingLabelProps, FloatingLabel, Form } from 'react-bootstrap';
import { useFormContext, FieldValues, Path } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { getError, removeNumbers } from './FormHelpers';
import { useWizardFormContext } from '@odmonorepo/core-components';
import { trackField } from 'merchant-components';
import { ReactNode } from 'react';

interface FormSelectProps<TFieldValues extends FieldValues> extends Omit<FloatingLabelProps, 'label'> {
  name: Path<TFieldValues>;
  options?: string[] | number[] | object[];
  label?: React.ReactNode;
  showLabel?: boolean;
  blankLabel?: string;
  required?: boolean;
  disabled?: boolean;
  useYupErrors?: boolean;
  useValueAsDisplay?: boolean;
  helperText?: ReactNode;
}

export const FormSelect = <TFieldValues extends FieldValues>({
  name,
  required = true,
  disabled = false,
  options = [],
  useYupErrors = false,
  showLabel = true,
  blankLabel = "",
  label = "",
  useValueAsDisplay = false,
  helperText,
  ...props
}: FormSelectProps<TFieldValues>) => {
  const { register, clearErrors } = useFormContext<TFieldValues>();
  const { formatMessage } = useIntl();
  const { formName } = useWizardFormContext() || { formName: '' };

  const onInput = (e: any) => e.target.value && clearErrors(name);

  // Destructure onBlur from register to handle it explicitly
  const { onBlur: registeredOnBlur, ...restRegister } = register(name);

  const selectElement = (
    <Form.Select
      required={required}
      onInput={onInput}
      onBlur={(e) => {
        trackField(
          formName,
          name as string,
          'Blur'
        );
        if (registeredOnBlur) {
          registeredOnBlur(e); // Call the original onBlur from react-hook-form
        }
      }}
      disabled={disabled}
      {...restRegister}
      isInvalid={!!getError(name)}
      form={formName || ''}
      {...props}
    >
      {blankLabel && <option value="">{blankLabel}</option>}
      {options.map((o) => (
        typeof o === 'object' && useValueAsDisplay ? (
          <option key={name + o.value} value={o.value}>{o.label}</option>
        ) : (
          <option value={useValueAsDisplay ? o : formatMessage({ id: 'formSelect.option.' + o + '.value', defaultMessage: formatMessage({ id: 'formSelect.option.' + o }) })} key={name + o}>
            {useValueAsDisplay ? o : <FormattedMessage id={'formSelect.option.' + o} />}
          </option>
        )
      ))}
    </Form.Select>
  );

  return showLabel ? (
    <FloatingLabel controlId={name} {...props} label={label ? label : <FormattedMessage id={'formSelect.' + removeNumbers(name)} />}>
      {selectElement}
      {helperText && (
        <div className="text-gray fs-8 mt-2">
          {typeof helperText === 'boolean' ? <FormattedMessage id={'formSelect.' + removeNumbers(name) + '.helperText'} /> : helperText}
        </div>
      )}
      <Form.Control.Feedback type="invalid">
        {useYupErrors && getError(name)?.message ? (
          <FormattedMessage id={getError(name).message} defaultMessage={getError(name).message} />
        ) : (
          <FormattedMessage id={'formSelect.' + removeNumbers(name) + '.error'} defaultMessage={formatMessage({ id: 'form.selection.error' })} />
        )}
      </Form.Control.Feedback>
    </FloatingLabel>
  ) : (
    <>
      {selectElement}
      <Form.Control.Feedback type="invalid">
        {useYupErrors && getError(name)?.message ? (
          <FormattedMessage id={getError(name).message} defaultMessage={getError(name).message} />
        ) : (
          <FormattedMessage id={'formSelect.' + removeNumbers(name) + '.error'} defaultMessage={formatMessage({ id: 'form.selection.error' })} />
        )}
      </Form.Control.Feedback>
      {helperText && (
        <div className="text-gray fs-8 mt-2">
          {typeof helperText === 'boolean' ? <FormattedMessage id={'formSelect.' + removeNumbers(name) + '.helperText'} /> : helperText}
        </div>
      )}
    </>
  );
};

export default FormSelect;
